// My Timesheet — self day-end reporting for the logged-in employee.
// Route: /employee/timesheet  (sidebar: "My Timesheet")
// Mirrors the attached HRBuddie timesheet mockup: a daily report with multiple
// tasks, achievements / blockers / next-day plan, draft + submit, and an
// immutable manager-review audit trail surfaced on the detail view.

import { useState } from "react";
import { Clock, Edit, Trash2, Eye, History, Plus, X } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { TIMESHEET_URL } from "../../constants/api";

const PRODUCTIVITY_OPTIONS = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const EDITABLE_STATUSES = ["draft", "pending", "correction"];

const STATUS_CONFIG = {
    draft: { text: "Draft", className: "bg-gray-100 text-gray-600 border-gray-200" },
    pending: { text: "Pending Review", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    approved: { text: "Approved", className: "bg-green-100 text-green-700 border-green-200" },
    rejected: { text: "Rejected", className: "bg-red-100 text-red-700 border-red-200" },
    correction: { text: "Correction Requested", className: "bg-blue-100 text-blue-700 border-blue-200" },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || {
        text: status ? status.charAt(0).toUpperCase() + status.slice(1) : "-",
        className: "bg-blue-100 text-blue-700 border-blue-200",
    };
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${cfg.className}`}>
            {cfg.text}
        </span>
    );
};

const emptyTask = () => ({ description: "", module: "", time_spent: "" });

const blankForm = () => ({
    date: dayjs(),
    project: "",
    actual_hours: "",
    productivity: "High",
    achievements: "",
    blockers: "",
    next_day_plan: "",
    tasks: [emptyTask()],
});

const EmployeeTimesheet = () => {
    const { create, update } = useApiMethods();

    // table
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    // dialogs
    const [openForm, setOpenForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [formState, setFormState] = useState(blankForm());
    const [loading, setLoading] = useState(false);

    const [openDetail, setOpenDetail] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);

    const [showDelete, setShowDelete] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);

    // ---- form helpers -----------------------------------------------------
    const handleChange = (field, value) => setFormState((prev) => ({ ...prev, [field]: value }));

    const handleTaskChange = (index, field, value) => {
        setFormState((prev) => {
            const tasks = [...prev.tasks];
            tasks[index] = { ...tasks[index], [field]: value };
            return { ...prev, tasks };
        });
    };

    const addTask = () => setFormState((prev) => ({ ...prev, tasks: [...prev.tasks, emptyTask()] }));

    const removeTask = (index) =>
        setFormState((prev) => {
            const tasks = prev.tasks.filter((_, i) => i !== index);
            return { ...prev, tasks: tasks.length ? tasks : [emptyTask()] };
        });

    const resetForm = () => {
        setIsEdit(false);
        setEditRecord(null);
        setFormState(blankForm());
    };

    const openCreate = () => {
        resetForm();
        setOpenForm(true);
    };

    const openEdit = (row) => {
        setIsEdit(true);
        setEditRecord(row);
        setFormState({
            date: row.date ? dayjs(row.date) : dayjs(),
            project: row.project || "",
            actual_hours: row.actual_hours ?? "",
            productivity: row.productivity || "High",
            achievements: row.achievements || "",
            blockers: row.blockers || "",
            next_day_plan: row.next_day_plan || "",
            tasks: row.tasks?.length
                ? row.tasks.map((t) => ({
                    description: t.description || "",
                    module: t.module || "",
                    time_spent: t.time_spent ?? "",
                }))
                : [emptyTask()],
        });
        setOpenForm(true);
    };

    // ---- submit -----------------------------------------------------------
    const buildPayload = (isDraft) => {
        const validTasks = formState.tasks
            .filter((t) => (t.description || "").trim())
            .map((t) => ({
                description: t.description,
                module: t.module,
                time_spent: t.time_spent === "" ? 0 : Number(t.time_spent),
            }));
        return {
            date: dayjs(formState.date).format("YYYY-MM-DD"),
            project: formState.project,
            actual_hours: formState.actual_hours === "" ? 0 : Number(formState.actual_hours),
            productivity: formState.productivity,
            achievements: formState.achievements,
            blockers: formState.blockers,
            next_day_plan: formState.next_day_plan,
            tasks: validTasks,
            is_draft: isDraft,
        };
    };

    const submit = async (isDraft) => {
        if (!formState.date) {
            ToastMsg("error", "Please select a date");
            return;
        }
        const validTasks = formState.tasks.filter((t) => (t.description || "").trim());
        if (!isDraft) {
            if (!validTasks.length) {
                ToastMsg("error", "Add at least one task");
                return;
            }
            if (!formState.actual_hours || Number(formState.actual_hours) <= 0) {
                ToastMsg("error", "Enter actual working hours");
                return;
            }
            // The total time logged against tasks must reconcile with the
            // actual working hours entered for the day.
            const totalTaskHours = validTasks.reduce(
                (sum, t) => sum + (Number(t.time_spent) || 0),
                0
            );
            if (Math.abs(totalTaskHours - Number(formState.actual_hours)) > 0.01) {
                ToastMsg(
                    "error",
                    `Task hours (${totalTaskHours}h) must match the actual working hours (${formState.actual_hours}h)`
                );
                return;
            }
        }

        const payload = buildPayload(isDraft);
        if (isEdit && editRecord?.id) payload.id = editRecord.id;

        setLoading(true);
        try {
            const res = isEdit
                ? await update(TIMESHEET_URL, payload)
                : await create(TIMESHEET_URL, payload);

            if (res?.status === "success") {
                ToastMsg("success", res.message || "Saved successfully");
                setOpenForm(false);
                resetForm();
                setServerTrigger((p) => !p);
            } else {
                ToastMsg("error", res?.message || "Failed to save timesheet");
            }
        } catch (e) {
            ToastMsg("error", "Something went wrong while saving");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteRecord) return;
        setLoading(true);
        try {
            // Soft delete via the PUT is_active flag (same convention as other
            // request pages; backend allows it only for draft/pending rows).
            const res = await update(TIMESHEET_URL, { id: deleteRecord.id, is_active: false });
            if (res?.status === "success") {
                ToastMsg("success", "Timesheet deleted successfully");
                setShowDelete(false);
                setDeleteRecord(null);
                setServerTrigger((p) => !p);
            } else {
                ToastMsg("error", res?.message || "Failed to delete");
            }
        } catch (e) {
            ToastMsg("error", "Something went wrong while deleting");
        } finally {
            setLoading(false);
        }
    };

    // ---- columns ----------------------------------------------------------
    const columns = [
        {
            Header: "Date",
            accessor: "date",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => (
                <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {value ? dayjs(value).format("DD MMM YYYY") : "-"}
                </span>
            ),
        },
        {
            Header: "Project / Module",
            accessor: "project",
            sortable: true,
            filterable: true,
            width: "220px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value || "-"}</span>,
        },
        {
            Header: "Hours",
            accessor: "actual_hours",
            sortable: true,
            width: "90px",
            Cell: ({ value }) => <span className="font-medium text-gray-700 dark:text-gray-300">{value ?? 0}h</span>,
        },
        {
            Header: "Tasks",
            accessor: "tasks",
            width: "80px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value?.length || 0}</span>,
        },
        {
            Header: "Productivity",
            accessor: "productivity",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => <span className="text-gray-700 dark:text-gray-300">{value || "-"}</span>,
        },
        {
            Header: "Status",
            accessor: "status",
            sortable: true,
            filterable: true,
            width: "150px",
            Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
            Header: "Actions",
            accessor: "actions",
            sortable: false,
            fixed: true,
            align: "right",
            width: "120px",
            Cell: ({ row }) => {
                const canEdit = EDITABLE_STATUSES.includes(row.status);
                const canDelete = ["draft", "pending"].includes(row.status);
                return (
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setDetailRecord(row);
                                setOpenDetail(true);
                            }}
                            className="h-7 w-7 p-0 rounded-md bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100"
                            title="View Report"
                        >
                            {row.status === "pending" ? <Eye className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
                        </Button>
                        {canEdit && (
                            <Button
                                variant="ghost"
                                onClick={() => openEdit(row)}
                                className="h-7 w-7 p-0 rounded-md bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
                                title="Edit Report"
                            >
                                <Edit className="h-3.5 w-3.5" />
                            </Button>
                        )}
                        {canDelete && (
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setDeleteRecord(row);
                                    setShowDelete(true);
                                }}
                                className="h-7 w-7 p-0 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                                title="Delete Report"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Clock}
                title="My Timesheet"
                subtitle="Submit your daily day-end report and track its approval"
                totalRecords={totalRecords}
                buttonEnable
                setOpenDialog={openCreate}
                buttonName="Add Day-End Report"
            />

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                serverSide
                url={TIMESHEET_URL}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize="md"
                isCSVRequired
                isPDFRequired
                isFromToDateRequired={false}
            />

            {/* Create / Edit dialog */}
            <DialogModal
                open={openForm}
                onClose={() => {
                    setOpenForm(false);
                    resetForm();
                }}
                onSave={() => submit(false)}
                onDraft={isEdit ? undefined : () => submit(true)}
                editMode={isEdit}
                title={isEdit ? "Edit Day-End Report" : "Submit Day-End Report"}
                saveLabel={isEdit ? "Update Report" : "Submit Report"}
                draftLabel="Save Draft"
                width="82rem"
                loading={loading}
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-h-[65vh] overflow-auto">
                    {/* Left — report fields */}
                    <div className="space-y-4 overflow-y-auto pr-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Date <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={formState.date}
                                    onChange={(v) => handleChange("date", v)}
                                    className="w-full"
                                    size="large"
                                    format="YYYY-MM-DD"
                                    allowClear={false}
                                    // A day-end report can only be filed for today
                                    // or yesterday — no future dates, nothing older.
                                    disabledDate={(current) =>
                                        !!current &&
                                        (current.isAfter(dayjs().endOf("day")) ||
                                            current.isBefore(dayjs().subtract(1, "day").startOf("day")))
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Project / Module
                                </label>
                                <Input
                                    value={formState.project}
                                    onChange={(e) => handleChange("project", e.target.value)}
                                    placeholder="e.g. HRMS Portal / Appraisal"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Actual Working Hours <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.5"
                                    value={formState.actual_hours}
                                    onChange={(e) => handleChange("actual_hours", e.target.value)}
                                    placeholder="8.0"
                                    className="w-full"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Self-rated Productivity
                                </label>
                                <Select
                                    value={formState.productivity}
                                    onChange={(v) => handleChange("productivity", v)}
                                    className="w-full"
                                    size="large"
                                    options={PRODUCTIVITY_OPTIONS}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Achievements / Completions
                            </label>
                            <Textarea
                                value={formState.achievements}
                                onChange={(e) => handleChange("achievements", e.target.value)}
                                placeholder="What did you complete today?"
                                rows={2}
                                maxLength={1000}
                                showCharCount
                                className="w-full min-h-28"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Blockers or Dependencies
                            </label>
                            <Textarea
                                value={formState.blockers}
                                onChange={(e) => handleChange("blockers", e.target.value)}
                                placeholder="Anything blocking you?"
                                rows={2}
                                maxLength={1000}
                                showCharCount
                                className="w-full min-h-28"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Planned Activities for Next Day
                            </label>
                            <Textarea
                                value={formState.next_day_plan}
                                onChange={(e) => handleChange("next_day_plan", e.target.value)}
                                placeholder="Tomorrow's plan..."
                                rows={2}
                                maxLength={1000}
                                showCharCount
                                className="w-full min-h-28"
                            />
                        </div>
                    </div>

                    {/* Right — tasks (own scroll container) */}
                    <div className="flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/30 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                Tasks Worked On <span className="text-red-500">*</span>
                            </label>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addTask}
                                className="text-sidebar border-sidebar/30 hover:bg-sidebar/10"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Add Task
                            </Button>
                        </div>

                        {/* Running total vs actual working hours */}
                        {(() => {
                            const taskHoursTotal = formState.tasks.reduce(
                                (sum, t) => sum + (Number(t.time_spent) || 0),
                                0
                            );
                            const actual = Number(formState.actual_hours) || 0;
                            const matched = Math.abs(taskHoursTotal - actual) <= 0.01;
                            return (
                                <div
                                    className={`mb-3 flex items-center justify-between rounded-md border px-3 py-1.5 text-xs font-medium ${matched
                                        ? "bg-green-50 text-green-700 border-green-200"
                                        : "bg-red-50 text-red-700 border-red-200"
                                        }`}
                                >
                                    <span>Task hours total: {taskHoursTotal}h</span>
                                    <span>
                                        {matched
                                            ? "Matches working hours"
                                            : `Should equal ${actual}h`}
                                    </span>
                                </div>
                            );
                        })()}

                        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
                            {formState.tasks.map((task, index) => (
                                <div
                                    key={index}
                                    className="relative rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900"
                                >
                                    {formState.tasks.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeTask(index)}
                                            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-md bg-red-50 text-red-500 hover:bg-red-100"
                                            title="Remove task"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                    <div className="space-y-2 mb-2 pr-6">
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            Task #{index + 1} — Description
                                        </label>
                                        <Input
                                            value={task.description}
                                            onChange={(e) => handleTaskChange(index, "description", e.target.value)}
                                            placeholder="What did you work on?"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="space-y-2">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                Module Detail
                                            </label>
                                            <Input
                                                value={task.module}
                                                onChange={(e) => handleTaskChange(index, "module", e.target.value)}
                                                placeholder="Module"
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                Time Spent (hrs)
                                            </label>
                                            <Input
                                                type="number"
                                                min={0}
                                                step="0.5"
                                                value={task.time_spent}
                                                onChange={(e) => handleTaskChange(index, "time_spent", e.target.value)}
                                                placeholder="2.0"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                    {
                                        index === formState.tasks.length - 1 &&
                                        formState.tasks.length > 2 && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-sidebar border-sidebar/30 hover:bg-sidebar/10 mt-2"
                                                onClick={addTask}
                                            >
                                                <Plus className="h-3.5 w-3.5 mr-1" />
                                                Add Task
                                            </Button>
                                        )
                                    }
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </DialogModal>

            {/* Detail dialog */}
            <DialogModal
                open={openDetail}
                onClose={() => setOpenDetail(false)}
                title="Report Detail"
                showActions={false}
                width="83rem"
            >
                {detailRecord && (
                    <div className="grid col-span-2 grid-cols-[70%_30%] gap-3 max-h-[75vh]">
                        {/* Left — report details */}
                        <div className="space-y-4 overflow-y-auto mb-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {detailRecord.date ? dayjs(detailRecord.date).format("DD MMM YYYY") : "-"}
                                    {detailRecord.project ? ` • ${detailRecord.project}` : ""}
                                </p>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        {detailRecord.actual_hours ?? 0}h • {detailRecord.productivity || "-"}
                                    </span>
                                    <StatusBadge status={detailRecord.status} />
                                </div>
                            </div>

                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hrs</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {(detailRecord.tasks || []).map((t, i) => (
                                            <tr key={i}>
                                                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{t.description}</td>
                                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{t.module || "-"}</td>
                                                <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">{t.time_spent ?? 0}h</td>
                                            </tr>
                                        ))}
                                        {!detailRecord.tasks?.length && (
                                            <tr>
                                                <td colSpan={3} className="px-3 py-4 text-center text-gray-400">No tasks</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <DetailRow label="Achievements" value={detailRecord.achievements} />
                            <DetailRow label="Blockers" value={detailRecord.blockers} />
                            <DetailRow label="Next Day Plan" value={detailRecord.next_day_plan} />
                            {detailRecord.manager_comment && (
                                <div className="rounded-lg border border-sidebar/30 bg-sidebar/5 px-3 py-2 text-sm">
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">Manager Comment: </span>
                                    <span className="text-gray-600 dark:text-gray-400">{detailRecord.manager_comment}</span>
                                </div>
                            )}
                        </div>

                        {/* Right — approval log */}
                        <ApprovalLogPanel logs={detailRecord.approval_logs} />
                    </div>
                )}
            </DialogModal>

            {/* Delete confirm */}
            <DialogModal
                open={showDelete}
                onClose={() => {
                    setShowDelete(false);
                    setDeleteRecord(null);
                }}
                onSave={handleDelete}
                title="Delete Day-End Report"
                saveLabel="Delete"
                width="420px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="text-center py-2">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this day-end report? This action cannot be undone.
                    </p>
                    {deleteRecord && (
                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md text-sm text-gray-700 dark:text-gray-300">
                            {deleteRecord.date ? dayjs(deleteRecord.date).format("DD MMM YYYY") : "-"} • {deleteRecord.project || "-"}
                        </div>
                    )}
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm">
        <span className="font-semibold text-gray-700 dark:text-gray-300">{label}: </span>
        <span className="text-gray-600 dark:text-gray-400">{value || "—"}</span>
    </div>
);

// Right-hand approval-trail container, shown alongside the report details.
export const ApprovalLogPanel = ({ logs }) => (
    <div className="flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/30 p-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <History className="h-4 w-4" /> Approval History
        </div>
        <div className="space-y-2 overflow-y-auto pr-1 flex-1">
            {logs?.length > 0 ? (
                logs.map((log, i) => (
                    <div
                        key={i}
                        className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">{log.action}</span>
                            <span className="text-gray-400">
                                {log.performed_at ? new Date(log.performed_at).toLocaleString() : ""}
                            </span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 mt-1">
                            {log.old_value || log.new_value ? (
                                <span>
                                    <span className="line-through text-red-500">{log.old_value || "—"}</span>
                                    {" → "}
                                    <span className="text-green-600 font-medium">{log.new_value || "—"}</span>
                                </span>
                            ) : null}
                            {log.reason ? <span className="italic"> • {log.reason}</span> : null}
                            {log.performed_by ? <span> • {log.performed_by}</span> : null}
                        </div>
                    </div>
                ))
            ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                    No approval history yet
                </div>
            )}
        </div>
    </div>
);

export default EmployeeTimesheet;
